import { useEffect, useRef, useState } from 'react';
import {
  GripVertical,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { blobToDataUrl, uploadFileToServer } from '../../utils/server-upload';
import type { Attachment } from './types';

interface SortableImageItemProps {
  id: string;
  imageUrl: string;
  onRemove: () => void;
}

function SortableImageItem({ id, imageUrl, onRemove }: SortableImageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group"
    >
      <div className="w-24 h-24 rounded-md overflow-hidden border-2 border-muted bg-muted hover:border-primary/50 transition-colors">
        <img
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            console.error('[SortableImageItem] Image failed to load:', imageUrl);
            e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect fill="%23ccc" width="96" height="96"/><text x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23666" font-size="12">Error</text></svg>';
          }}
        />
      </div>
      {/* Remove button */}
      <button
        onClick={onRemove}
        className="absolute -top-2 -right-2 w-6 h-6 bg-background border border-border rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-muted z-10"
        title="Remove image"
      >
        <X className="h-3 w-3 text-destructive" />
      </button>
      {/* Drag handle - always visible but subtle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 w-6 h-6 bg-black/50 rounded flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-black/70 transition-colors"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4 text-white" />
      </div>
    </div>
  );
}

interface AddImageCardProps {
  onAddFromUrl: () => void;
  onAddFromFile: () => void;
  isUploading: boolean;
}

function AddImageCard({ onAddFromUrl, onAddFromFile, isUploading }: AddImageCardProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={isUploading}
          className="w-24 h-24 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-colors flex flex-col items-center justify-center gap-1 group disabled:opacity-50 disabled:cursor-not-allowed"
          title="Add image"
        >
          <Plus className="h-6 w-6 text-primary transition-colors" />
          <span className="text-xs font-medium text-primary">Add</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2"
            onClick={() => {
              onAddFromFile();
              setOpen(false);
            }}
          >
            <Upload className="h-4 w-4" />
            From device
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2"
            onClick={() => {
              onAddFromUrl();
              setOpen(false);
            }}
          >
            <Upload className="h-4 w-4" />
            From URL
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface ImageManagerProps {
  attachments: Attachment[];
  onAttachmentsChange: (attachments: Attachment[]) => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
  uploadError: string | null;
  setUploadError: (error: string | null) => void;
  dropZoneRef?: React.RefObject<HTMLDivElement | null>;
}

export function ImageManager({
  attachments,
  onAttachmentsChange,
  isUploading,
  setIsUploading,
  uploadError,
  setUploadError,
  dropZoneRef,
}: ImageManagerProps) {
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag and drop setup for image reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // File drag and drop from desktop
  useEffect(() => {
    const dropZone = dropZoneRef?.current;
    if (!dropZone) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      setIsDraggingFile(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingFile(false);
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingFile(false);

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (!file.type.startsWith('image/')) {
        setUploadError('Please drop an image file');
        return;
      }

      setIsUploading(true);
      setUploadError(null);

      try {
        const dataUrl = await blobToDataUrl(file);
        const uploadResult = await uploadFileToServer({
          dataUrl,
          mimeType: file.type,
          filename: file.name
        });

        const newAttachment: Attachment = { url: uploadResult.url, type: 'image' };
        onAttachmentsChange([...attachments, newAttachment]);
      } catch (error) {
        console.error('File drop error:', error);
        setUploadError((error as Error).message || 'Failed to upload file');
      } finally {
        setIsUploading(false);
      }
    };

    dropZone.addEventListener('dragover', handleDragOver);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', handleDrop);

    return () => {
      dropZone.removeEventListener('dragover', handleDragOver);
      dropZone.removeEventListener('dragleave', handleDragLeave);
      dropZone.removeEventListener('drop', handleDrop);
    };
  }, [attachments, dropZoneRef, onAttachmentsChange, setIsUploading, setUploadError]);

  const handleAddImageFromUrl = async () => {
    if (!imageUrl.trim()) {
      setUploadError('Please enter an image URL');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const dataUrl = await blobToDataUrl(blob);

      const uploadResult = await uploadFileToServer({
        dataUrl,
        mimeType: blob.type || 'image/jpeg',
        filename: `image-${Date.now()}.jpg`
      });

      const newAttachment: Attachment = { url: uploadResult.url, type: 'image' };
      onAttachmentsChange([...attachments, newAttachment]);

      setImageUrl('');
      setShowImageInput(false);
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadError((error as Error).message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddImageFromFile = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setUploadError('Please select a file');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPEG, PNG, GIF, WebP)');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: 10MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const dataUrl = await blobToDataUrl(file);
      const uploadResult = await uploadFileToServer({
        dataUrl,
        mimeType: file.type || 'image/jpeg',
        filename: file.name
      });

      // Verify URL is valid
      if (!uploadResult.url) {
        throw new Error('Upload failed: No URL returned from server');
      }

      if (!uploadResult.url.startsWith('http')) {
        throw new Error(`Invalid URL format: ${uploadResult.url}`);
      }

      const newAttachment: Attachment = { url: uploadResult.url, type: 'image' };
      onAttachmentsChange([...attachments, newAttachment]);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('[Image Upload] Error:', error);
      setUploadError((error as Error).message || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    const imageAttachments = attachments.filter(a => a.type === 'image');
    const updatedImages = imageAttachments.filter((_, i) => i !== index);
    const nonImageAttachments = attachments.filter(a => a.type !== 'image');
    onAttachmentsChange([...nonImageAttachments, ...updatedImages]);
  };

  const handleImageDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const imageAttachments = attachments.filter(a => a.type === 'image');
    if (imageAttachments.length === 0) return;

    const imageUrls = imageAttachments.map(a => a.url);
    const oldIndex = imageUrls.indexOf(active.id as string);
    const newIndex = imageUrls.indexOf(over.id as string);

    if (oldIndex !== newIndex && oldIndex >= 0 && newIndex >= 0) {
      const reorderedImages = [...imageAttachments];
      const [removed] = reorderedImages.splice(oldIndex, 1);
      reorderedImages.splice(newIndex, 0, removed);

      const nonImageAttachments = attachments.filter(a => a.type !== 'image');
      onAttachmentsChange([...nonImageAttachments, ...reorderedImages]);
    }
  };

  const imageAttachments = attachments.filter(a => a.type === 'image');
  const hasImages = imageAttachments.length > 0;

  return (
    <div>
      <label className="text-sm font-medium mb-2 block">Media</label>

      {/* Drag overlay */}
      {isDraggingFile && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg z-50 pointer-events-none">
          <div className="text-center">
            <Upload className="h-12 w-12 text-primary mx-auto mb-2" />
            <p className="text-lg font-medium">Drop image here</p>
            <p className="text-sm text-muted-foreground">Release to upload</p>
          </div>
        </div>
      )}

      {/* Show prominent upload area if no images */}
      {!hasImages ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center bg-primary/5 hover:bg-primary/10 hover:border-primary/50 transition-colors cursor-pointer"
        >
          <Upload className="h-6 w-6 text-primary mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground mb-3">
            Click to upload or drag and drop
          </p>
          <div className="flex gap-2 justify-center" onClick={(e) => e.stopPropagation()}>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              variant="default"
              size="sm"
            >
              <Upload className="h-4 w-4 mr-2" />
              From device
            </Button>
            <Button
              onClick={() => setShowImageInput(true)}
              disabled={isUploading}
              variant="outline"
              size="sm"
            >
              From URL
            </Button>
          </div>
        </div>
      ) : (
        /* Image thumbnails with drag and drop */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleImageDragEnd}
        >
          <SortableContext
            items={imageAttachments.map(a => a.url)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="flex flex-wrap gap-3">
              {/* Existing images */}
              {imageAttachments.map((attachment, index) => (
                <SortableImageItem
                  key={attachment.url}
                  id={attachment.url}
                  imageUrl={attachment.url}
                  onRemove={() => handleRemoveImage(index)}
                />
              ))}

              {/* Add image card */}
              <AddImageCard
                onAddFromUrl={() => setShowImageInput(true)}
                onAddFromFile={() => fileInputRef.current?.click()}
                isUploading={isUploading}
              />
            </div>
          </SortableContext>
        </DndContext>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleAddImageFromFile}
        className="hidden"
      />

      {/* URL input dialog */}
      {showImageInput && (
        <div className="mt-3 p-3 border rounded-md bg-muted/30 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Add Image from URL</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowImageInput(false);
                setImageUrl('');
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddImageFromUrl()}
            placeholder="https://example.com/image.jpg"
            disabled={isUploading}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm bg-card disabled:bg-muted disabled:cursor-not-allowed"
            autoFocus
          />
          <Button
            onClick={handleAddImageFromUrl}
            disabled={isUploading || !imageUrl.trim()}
            className="w-full"
            size="sm"
          >
            {isUploading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                <span>Uploading...</span>
              </>
            ) : (
              <span>Add Image</span>
            )}
          </Button>
        </div>
      )}

      {uploadError && (
        <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          ⚠ {uploadError}
        </div>
      )}
    </div>
  );
}
